/**
 * Phone Store
 *
 * Manages Twilio Voice SDK device lifecycle, call state,
 * and phone history (calls, SMS, voicemail).
 */
import { defineStore } from 'pinia'
import { ref, shallowRef, computed } from 'vue'
import { twilioService } from '../services/twilio.service'
import type { CallLogEntry, SMSConversation, Voicemail } from '../services/twilio.service'
import { registerIdentityReset } from './auth'

export type PhoneStatus = 'offline' | 'connecting' | 'ready' | 'on-call' | 'incoming' | 'ringing' | 'error'

export const usePhoneStore = defineStore('phone', () => {
  // ===== State =====
  const status = ref<PhoneStatus>('offline')
  const device = shallowRef<any>(null)  // Twilio Device instance (shallowRef to avoid Vue proxy conflicts)
  const activeCall = shallowRef<any>(null)  // Twilio Call instance
  const activeCallSid = ref<string | null>(null)  // Tracked separately — call.parameters?.CallSid may not be set immediately
  const incomingCall = shallowRef<any>(null)

  // Extract CallSid from various sources on the Twilio Call object.
  // For outbound browser calls, parameters.CallSid may not be set until ringing/accept.
  function extractCallSid(call: any): string | null {
    return call?.parameters?.CallSid
      || call?.outboundConnectionId
      || call?._mediaHandler?._callSid
      || null
  }
  const identity = ref<string>('')
  const callDuration = ref(0)
  const isMuted = ref(false)
  const outputVolume = ref(100)  // 0-100 speaker volume (separate from mute which controls mic)
  const activeCallNotes = ref('')
  const error = ref<string | null>(null)

  // History
  const callHistory = ref<CallLogEntry[]>([])
  const smsConversations = ref<SMSConversation[]>([])
  const voicemails = ref<Voicemail[]>([])

  // UI
  const isExpanded = ref(false)
  const activeTab = ref<'dialer' | 'calls' | 'sms' | 'voicemail'>('dialer')

  // Call timer interval
  let timerInterval: ReturnType<typeof setInterval> | null = null

  // The device, its Twilio identity and the three history lists belong to ONE
  // signed-in user (RV-FE N4). Every change of who is signed in tears the
  // device down and drops the lists; `generation` drops a token or history
  // read the previous user started.
  let generation = 0
  registerIdentityReset(() => {
    generation += 1
    destroy()
  })

  // ===== Getters =====
  const isReady = computed(() => status.value === 'ready')
  const isOnCall = computed(() => status.value === 'on-call')
  const hasIncoming = computed(() => status.value === 'incoming')
  const unreadCount = computed(() => {
    const unreadSMS = smsConversations.value.reduce((sum, c) => sum + c.unread_count, 0)
    const unreadVM = voicemails.value.filter(v => !v.is_read).length
    return unreadSMS + unreadVM
  })

  // ===== Actions =====

  /**
   * Initialize Twilio Device with access token.
   * Must be called after user authentication.
   */
  async function initialize(): Promise<void> {
    if (device.value) return

    try {
      status.value = 'connecting'

      // Get token from backend
      const gen = generation
      const tokenData = await twilioService.getToken()
      if (gen !== generation) return
      identity.value = tokenData.identity

      // Dynamic import to avoid loading SDK on non-ops pages
      const { Device } = await import('@twilio/voice-sdk')

      const newDevice = new Device(tokenData.token, {
        edge: 'ashburn',
        closeProtection: true,
        logLevel: 'debug',
      })

      newDevice.on('registered', () => {
        console.log('[Phone] Device registered successfully')
        status.value = 'ready'
        error.value = null
      })

      newDevice.on('incoming', (call: any) => {
        incomingCall.value = call
        status.value = 'incoming'
        isExpanded.value = true
        activeTab.value = 'dialer'
        setupCallEvents(call)
      })

      newDevice.on('error', (twilioError: any) => {
        console.error('[Phone] Device error:', {
          code: twilioError.code,
          message: twilioError.message,
          causes: twilioError.causes,
          solutions: twilioError.solutions,
          description: twilioError.description,
          originalError: twilioError.originalError,
        })
        error.value = `${twilioError.message || 'Unknown error'} (${twilioError.code || '?'})`
        status.value = 'error'
      })

      newDevice.on('tokenWillExpire', async () => {
        const refreshData = await twilioService.getToken()
        newDevice.updateToken(refreshData.token)
      })

      await newDevice.register()
      if (gen !== generation) {
        // Signed out (or someone else signed in) while registering.
        newDevice.destroy()
        return
      }
      device.value = newDevice

      // Load history
      await refreshHistory()
    } catch (err: any) {
      console.error('[Phone] Initialize failed:', err)
      error.value = err.message
      status.value = 'error'
    }
  }

  async function makeCall(toNumber: string): Promise<void> {
    if (!device.value || !isReady.value) return

    try {
      // Pre-flight microphone check
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        stream.getTracks().forEach(t => t.stop())
        console.log('[Phone] Microphone access granted')
      } catch (micErr: any) {
        console.error('[Phone] Microphone access denied:', micErr)
        error.value = 'Microphone access denied. Please allow microphone in browser settings.'
        return
      }

      console.log('[Phone] Connecting call to:', toNumber)
      const call = await device.value.connect({
        params: { To: toNumber },
      })
      const sid = extractCallSid(call)
      console.log('[Phone] Call object, SID:', sid, 'params:', JSON.stringify(call.parameters))
      activeCall.value = call
      activeCallSid.value = sid
      status.value = 'on-call'
      startCallTimer()
      setupCallEvents(call)
    } catch (err: any) {
      console.error('[Phone] makeCall error:', {
        code: err.code,
        message: err.message,
        causes: err.causes,
        solutions: err.solutions,
        description: err.description,
      })
      error.value = err.message || 'Call failed'
    }
  }

  function acceptIncoming(): void {
    if (!incomingCall.value) return
    incomingCall.value.accept()
    activeCall.value = incomingCall.value
    activeCallSid.value = extractCallSid(incomingCall.value)
    incomingCall.value = null
    status.value = 'on-call'
    startCallTimer()
  }

  function rejectIncoming(): void {
    if (!incomingCall.value) return
    incomingCall.value.reject()
    incomingCall.value = null
    status.value = 'ready'
  }

  function hangup(): void {
    const callSid = activeCallSid.value || extractCallSid(activeCall.value)
    console.log('[Phone] Hangup requested, callSid:', callSid)
    activeCall.value?.disconnect()

    // Also terminate the call on Twilio's server side.
    // Browser disconnect() only drops the WebRTC leg — the SIP/PSTN
    // leg stays connected without this server-side termination.
    if (callSid) {
      twilioService.hangupCall(callSid).catch(err => {
        console.warn('[Phone] Failed to terminate call server-side:', err)
      })
    } else {
      console.warn('[Phone] No CallSid available for server-side termination')
    }
  }

  function toggleMute(): void {
    if (!activeCall.value) return
    const newMuted = !activeCall.value.isMuted()
    activeCall.value.mute(newMuted)
    isMuted.value = newMuted
  }

  function setOutputVolume(level: number): void {
    outputVolume.value = Math.max(0, Math.min(100, level))
    // Apply to all audio elements created by the Twilio Voice SDK
    document.querySelectorAll('audio').forEach(el => {
      el.volume = outputVolume.value / 100
    })
  }

  function sendDigits(digits: string): void {
    activeCall.value?.sendDigits(digits)
  }

  function setupCallEvents(call: any): void {
    call.on('accept', () => {
      const sid = extractCallSid(call)
      console.log('[Phone] Call accepted, SID:', sid)
      activeCallSid.value = sid || activeCallSid.value
      status.value = 'on-call'
    })
    call.on('disconnect', () => {
      console.log('[Phone] Call disconnected')
      activeCall.value = null
      activeCallSid.value = null
      incomingCall.value = null
      isMuted.value = false
      status.value = 'ready'
      stopCallTimer()
      refreshHistory()
    })
    call.on('cancel', () => {
      console.log('[Phone] Call cancelled')
      incomingCall.value = null
      activeCallSid.value = null
      status.value = 'ready'
    })
    call.on('error', (twilioError: any) => {
      console.error('[Phone] Call error:', {
        code: twilioError.code,
        message: twilioError.message,
        causes: twilioError.causes,
        solutions: twilioError.solutions,
      })
      error.value = `${twilioError.message || 'Call error'} (${twilioError.code || '?'})`
    })
    call.on('ringing', (hasEarlyMedia: boolean) => {
      const sid = extractCallSid(call)
      console.log('[Phone] Ringing, early media:', hasEarlyMedia, 'SID:', sid)
      activeCallSid.value = sid || activeCallSid.value
    })
    call.on('warning', (name: string) => {
      console.warn('[Phone] Call warning:', name)
    })
  }

  function startCallTimer(): void {
    callDuration.value = 0
    timerInterval = setInterval(() => {
      callDuration.value++
    }, 1000)
  }

  function stopCallTimer(): void {
    if (timerInterval) {
      clearInterval(timerInterval)
      timerInterval = null
    }
    callDuration.value = 0
  }

  async function refreshHistory(): Promise<void> {
    const gen = generation
    try {
      const [calls, convos, vms] = await Promise.all([
        twilioService.getCallHistory(),
        twilioService.getSMSConversations(),
        twilioService.getVoicemails(),
      ])
      if (gen !== generation) return
      callHistory.value = calls
      smsConversations.value = convos
      voicemails.value = vms
    } catch {
      // Silently fail - history is non-critical
    }
  }

  function destroy(): void {
    device.value?.destroy()
    device.value = null
    status.value = 'offline'
    stopCallTimer()
    // The lists and identity are the signed-in user's; a destroyed device
    // leaves none of them behind (RV-FE N4).
    identity.value = ''
    callHistory.value = []
    smsConversations.value = []
    voicemails.value = []
  }

  return {
    // State
    status, device, activeCall, activeCallSid, incomingCall, identity,
    callDuration, isMuted, outputVolume, activeCallNotes, error,
    callHistory, smsConversations, voicemails,
    isExpanded, activeTab,

    // Getters
    isReady, isOnCall, hasIncoming, unreadCount,

    // Actions
    initialize, makeCall, acceptIncoming, rejectIncoming,
    hangup, toggleMute, setOutputVolume, sendDigits,
    refreshHistory, destroy,
  }
})
